// Personnages et infectés animés. Modèles réalistes Mixamo (soldat, trois zombies) quand ils sont présents,
// sinon les modèles Quaternius (CC0). Le soldat se déplace dans 8 directions en visant (marche de côté, à reculons).
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { WEAPONS, modsKey, type Mods, type WeaponId } from '../shared/weapons';
import { WEAPON_MODELS, buildWeapon, weaponObject } from './weaponModels';
import { LowBody, PALETTES, type Palette, GEAR_IDS, GEAR_MODELS } from './lowbody';

export const SURVIVORS = ['Characters_Matt', 'Characters_Lis', 'Characters_Sam', 'Characters_Shaun'];
export const INFECTED_MODELS = ['Zombie_Basic', 'Zombie_Arm'];
// silex : récupérateur low-poly sculpté (riggé par Mixamo, animations du soldat) ; soldier : squelette habillé de blocs
const MODEL_V = 2; // à augmenter quand un modèle change (contourne le cache des navigateurs)
const MIXAMO = ['silex', 'soldier', 'zombie_a', 'zombie_b', 'zombie_c'];
const WEAPON_MESHES = ['Axe', 'Guitar', 'Knife', 'Pistol', 'Rifle', 'Shotgun', 'SMG', 'Spear', 'WoodenBat_Barbed', 'WoodenBat_Saw'];
const SCALE = 1.06;
const HEIGHT = 1.8; // m, taille des personnages Mixamo une fois mis à l'échelle
// Armes tenues par les personnages Mixamo : voir weaponModels.ts
const HELD = WEAPON_MODELS;
interface Rig { scene: THREE.Group; animations: THREE.AnimationClip[]; scale: number; mixamo: boolean }
const rigs = new Map<string, Rig>();
const heldModels = new Map<string, THREE.Object3D>();

// Les squelettes Mixamo s'appellent « mixamorig: », « mixamorig1 »… selon le fichier : on unifie
const cleanBone = (n: string) => n.replace(/^mixamorig\d*:?/, 'mixamorig');

export async function loadActors(onProgress?: (f: number) => void) {
  const loader = new GLTFLoader();
  const jobs: Promise<unknown>[] = [];
  let done = 0, total = 0;
  const job = (p: Promise<unknown>) => { total++; jobs.push(p.catch(() => undefined).finally(() => onProgress?.(++done / total))); };
  for (const n of [...SURVIVORS, ...INFECTED_MODELS]) job(loader.loadAsync(`assets/models/${n}.gltf`).then(g => rigs.set(n, { scene: g.scene, animations: g.animations, scale: SCALE, mixamo: false })));
  // ?v= : version des modèles ; les navigateurs qui gardaient l'ancien fichier en cache le rechargent
  for (const n of MIXAMO) job(loader.loadAsync(`assets/models/mixamo/${n}.glb?v=${MODEL_V}`).then(g => {
    g.scene.traverse(o => { o.name = cleanBone(o.name); });
    for (const c of g.animations) for (const t of c.tracks) t.name = cleanBone(t.name);
    const h = new THREE.Box3().setFromObject(g.scene).getSize(new THREE.Vector3()).y || 1;
    rigs.set(n, { scene: g.scene, animations: g.animations, scale: HEIGHT / h, mixamo: true });
  }));
  // Équipement sculpté de Silex (casques, gilets, sacs) : facultatif, l'équipement fait en code sert sinon
  for (const id of GEAR_IDS) job(loader.loadAsync(`assets/models/gear/${id}.glb?v=${MODEL_V}`).then(g => {
    g.scene.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh) for (const mat of [m.material].flat() as THREE.MeshStandardMaterial[]) { mat.flatShading = true; mat.needsUpdate = true; } });
    GEAR_MODELS.set(id, g.scene);
  }));
  // Armes : fabriquées en low-poly (build) ou chargées depuis un fichier
  for (const h of Object.values(HELD)) { if (h.build) heldModels.set(h.file, h.build()); else job(loader.loadAsync(`assets/models/weapons/${h.file}`).then(g => heldModels.set(h.file, g.scene))); }
  await Promise.all(jobs);
  // Les zombies Mixamo partagent les animations téléchargées avec le premier
  const za = rigs.get('zombie_a');
  if (za) for (const n of ['zombie_b', 'zombie_c']) { const r = rigs.get(n); if (r && !r.animations.length) r.animations = za.animations; }
}

// Modèle d'un survivant ou d'un infecté : Mixamo si présent
export const survivorModel = (i: number) => (rigs.has('silex') ? 'silex' : rigs.has('soldier') ? 'soldier' : SURVIVORS[i % SURVIVORS.length]!);
export const infectedModel = (kind: number, id: number) => (rigs.has('zombie_a') ? ['zombie_a', 'zombie_b', 'zombie_c'].filter(n => rigs.has(n))[id % 3] ?? 'zombie_a' : INFECTED_MODELS[kind] ?? INFECTED_MODELS[0]!);

export interface ActorState {
  x: number; z: number; y?: number; angle: number; moving: boolean; sprint: boolean; crouch: boolean; dead: boolean; // y : hauteur des pieds
  air?: boolean; // en plein saut
  vy?: number; // vitesse verticale (m/s) : monte ou retombe
  pitch?: number; // inclinaison de la visée (radians, + vers le haut) : le buste et l'arme suivent
  weapon: WeaponId | null; attack?: boolean; search?: boolean; helmet?: string | null; vest?: string | null; pack?: boolean | string | null; aim?: boolean; // helmet, vest : objet porté
  mods?: Mods; // accessoires de l'arme en main (visibles sur le modèle)
  reload?: boolean; heal?: boolean; // rechargement, soin en cours (gestes faits dans le code, pas d'animation Mixamo)
}

// Direction du déplacement par rapport au regard → suffixe de l'animation Mixamo (8 directions)
const SECTORS = ['f', 'fr', 'r', 'br', 'b', 'bl', 'l', 'fl'];

export class Actor {
  readonly root = new THREE.Group();
  private body: THREE.Object3D;
  private mixer: THREE.AnimationMixer | null = null;
  private actions = new Map<string, THREE.AnimationAction>();
  private current: THREE.AnimationAction | null = null;
  private weapons = new Map<string, THREE.Object3D>();
  private weapon: WeaponId | null | undefined = undefined;
  private dead = false;
  private recoil = 0;
  private last = new THREE.Vector2(NaN, NaN);
  private vel = new THREE.Vector2();
  private speed = 0;
  private mixamo = false;
  private scale = SCALE;
  private hand: THREE.Object3D | null = null;
  private held: THREE.Object3D | null = null;
  private deathClip = 'death_1';
  private crawl = false;
  // Infecté : taille (costaud plus grand) et posture (rampant)
  top = 1.8; // sommet du corps (m) : la visée sait ce qu'elle touche
  crouching = false;
  setKind(scale: number, crawl: boolean) { this.root.scale.setScalar(scale); this.crawl = crawl; this.top = crawl ? 0.6 : 1.8 * scale; }
  private reloadT = -1; // temps écoulé dans le rechargement (s), -1 si aucun
  private healT = -1;
  private gesture = 0; // poids du geste (0 à 1) : fondu d'entrée et de sortie
  private lastHand = new THREE.Vector3();
  private lastHeal = false;
  private low: LowBody | null = null; // corps low-poly fixé sur le squelette
  private leftHand: THREE.Object3D | null = null;
  readonly infected: boolean;

  constructor(scene: THREE.Scene, model: string, look?: Palette) {
    this.infected = model.toLowerCase().startsWith('zombie');
    const rig = rigs.get(model);
    if (rig) {
      this.mixamo = rig.mixamo;
      this.scale = rig.scale;
      this.body = SkeletonUtils.clone(rig.scene);
      this.body.scale.setScalar(rig.scale);
      this.body.traverse(o => {
        if ((o as THREE.Mesh).isMesh) { o.castShadow = true; o.frustumCulled = false; }
        if (!rig.mixamo && WEAPON_MESHES.includes(o.name)) { this.weapons.set(o.name, o); o.visible = false; }
      });
      this.mixer = new THREE.AnimationMixer(this.body);
      for (const c of rig.animations) this.actions.set(c.name, this.mixer.clipAction(c));
      for (const k of ['Death', 'Punch', 'death', 'attack', 'death_1', 'death_2', 'death_3']) {
        const a = this.actions.get(k);
        if (a) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
      }
      this.hand = this.body.getObjectByName('mixamorigRightHand') ?? null;
      this.leftHand = this.body.getObjectByName('mixamorigLeftHand') ?? null;
      // Modèle sculpté (silex) : sa propre peau, recolorée selon la tenue ; sinon des blocs sur le squelette
      if (rig.mixamo) this.low = new LowBody(this.body, look ?? (this.infected ? PALETTES.infected[0]! : PALETTES.soldier), this.infected, model === 'silex');
      this.deathClip = `death_${1 + Math.floor(Math.random() * 3)}`;
      this.play(this.mixamo ? 'idle' : this.infected ? 'Idle' : 'Idle_Gun');
    } else {
      const g = new THREE.Group();
      const m = new THREE.Mesh(new THREE.CapsuleGeometry(0.3, 1.1, 4, 10), new THREE.MeshStandardMaterial({ color: this.infected ? 0x6f8a5a : 0x8899aa }));
      m.position.y = 0.85;
      m.castShadow = true;
      g.add(m);
      this.body = g;
    }
    this.root.add(this.body);
    scene.add(this.root);
  }

  // Animation jouée une fois (saut, réception, rechargement) : from = point de départ (fraction de la durée), vitesse
  private playOnce(name: string, fade: number, from = 0, speed = 1) {
    const a = this.actions.get(name);
    if (!a) return;
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    a.reset();
    a.time = a.getClip().duration * from;
    a.timeScale = speed;
    a.fadeIn(fade).play();
    if (this.current && this.current !== a) this.current.fadeOut(fade);
    this.current = a;
  }
  // Saut en trois temps (impulsion, en l'air, réception), d'après la vitesse verticale réelle
  private airPhase: 'ground' | 'up' | 'air' = 'ground';
  private landUntil = 0;
  private airAt = 0; // dernier instant vu en l'air (les autres joueurs : la hauteur arrive par à-coups, on ne conclut pas trop vite)
  private jumpAnim(s: ActorState, moving: boolean): boolean {
    if (!this.actions.has('jump_air')) return false;
    const now = performance.now() / 1000;
    if (s.air) this.airAt = now;
    if (s.air || (this.airPhase !== 'ground' && now - this.airAt < 0.1)) {
      if (!s.air) return true;
      // Décollage : l'impulsion, prise au moment où les pieds quittent le sol (sans la flexion d'avant)
      if (this.airPhase === 'ground') { this.airPhase = 'up'; this.playOnce('jump_up', 0.06, 0.3, 1.5); }
      // Au sommet, ou si l'impulsion est finie : la pose en l'air, tenue jusqu'au sol
      else if (this.airPhase === 'up' && ((s.vy ?? 0) < 0.6 || (this.current && this.current.time >= this.current.getClip().duration * 0.95))) { this.airPhase = 'air'; this.play('jump_air', 0.15); }
      return true;
    }
    if (this.airPhase !== 'ground') {
      // Réception : genoux qui amortissent, plus brève si l'on repart en courant
      this.airPhase = 'ground';
      this.landUntil = now + (moving ? 0.2 : 0.38);
      this.playOnce('jump_land', 0.05, 0.12, moving ? 1.8 : 1.3);
    }
    return now < this.landUntil;
  }
  // Rechargement animé (fusils, à l'arrêt) : calé sur la durée de rechargement de l'arme
  private reloadAnim(s: ActorState, moving: boolean): boolean {
    const name = s.crouch ? 'reload_crouch' : 'reload';
    if (!s.reload || moving || !this.weapon || this.weapon === 'pistol' || !this.actions.has(name)) return false;
    const a = this.actions.get(name)!;
    if (this.current !== a) this.playOnce(name, 0.15, 0, Math.max(0.6, Math.min(2.2, a.getClip().duration / WEAPONS[this.weapon].reload)));
    return true;
  }
  private reloadClip(s: ActorState) { return !!s.reload && !(s.moving || this.speed > 0.4) && !!this.weapon && this.weapon !== 'pistol' && this.actions.has(s.crouch ? 'reload_crouch' : 'reload'); }

  private play(name: string, fade = 0.18) {
    const a = this.actions.get(name);
    if (!a || a === this.current) return;
    a.reset().fadeIn(fade).play();
    this.current?.fadeOut(fade);
    this.current = a;
  }

  private modsKey = '';
  setWeapon(w: WeaponId | null, mods?: Mods) {
    const mk = modsKey(mods);
    if (w === this.weapon && mk === this.modsKey) return;
    this.weapon = w;
    this.modsKey = mk;
    if (this.mixamo) {
      // Arme tenue : modèle à la taille réelle, placé à la main droite à chaque image
      this.held?.removeFromParent();
      this.held = null;
      const def = w ? HELD[w] : null;
      const src = def && heldModels.get(def.file);
      if (!def || !src) return;
      // Taille calculée sur l'arme nue : un silencieux allonge l'arme sans la rétrécir
      const size = new THREE.Box3().setFromObject(weaponObject(src, def)).getSize(new THREE.Vector3());
      const inner = def.build ? weaponObject(buildWeapon(w!, mods), def) : weaponObject(src, def);
      inner.scale.setScalar(def.len / Math.max(size.x, size.y, size.z));
      inner.traverse(o => { if ((o as THREE.Mesh).isMesh) o.castShadow = true; });
      const g = new THREE.Group();
      g.add(inner);
      this.root.add(g);
      this.held = g;
      return;
    }
    for (const m of this.weapons.values()) m.visible = false;
    const m = w ? this.weapons.get(WEAPONS[w].mesh) : null;
    if (m) m.visible = true;
  }

  fired() { this.recoil = 1; }
  get isDead() { return this.dead; }

  // Protections visibles : casque sur la tête, gilet sur le torse (suivent les os du squelette)
  private helmetMesh: THREE.Object3D | null = null;
  private vestMesh: THREE.Object3D | null = null;
  setArmor(helmetId: string | null, vestId: string | null, pack: boolean | string | null = false) {
    if (this.infected) return;
    if (this.low) { this.low.setGear(helmetId, vestId, pack); return; }
    const helmet = !!helmetId, vest = !!vestId;
    const head = this.body.getObjectByName(this.mixamo ? 'mixamorigHead' : 'Head'), torso = this.body.getObjectByName(this.mixamo ? 'mixamorigSpine2' : 'Torso');
    if (helmet && !this.helmetMesh && head) {
      const g = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0x4a5240, roughness: 0.7 }));
      g.castShadow = true;
      this.helmetMesh = g;
      head.add(g);
      this.fitTo(g, head, 'helmet');
    }
    if (vest && !this.vestMesh && torso) {
      const g = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: 0x3a3f36, roughness: 0.8 }));
      g.castShadow = true;
      this.vestMesh = g;
      torso.add(g);
      this.fitTo(g, torso, 'vest');
    }
    if (this.helmetMesh) this.helmetMesh.visible = helmet;
    if (this.vestMesh) this.vestMesh.visible = vest;
  }
  // Taille en mètres convertie dans le repère de l'os (dont l'échelle dépend du modèle)
  private fitTo(m: THREE.Object3D, bone: THREE.Object3D, kind: 'helmet' | 'vest') {
    this.body.updateMatrixWorld(true);
    const s = new THREE.Vector3();
    bone.getWorldScale(s);
    const k = 1 / (s.y || 1);
    if (this.mixamo) {
      if (kind === 'helmet') { m.scale.set(0.135 * k, 0.12 * k, 0.15 * k); m.position.set(0, 0.11 * k, 0.01 * k); }
      else { m.scale.set(0.38 * k, 0.38 * k, 0.26 * k); m.position.set(0, -0.08 * k, 0.1 * k); }
      return;
    }
    if (kind === 'helmet') { m.scale.setScalar(0.36 * k); m.position.set(0, 0.32 * k, 0); }
    else { m.scale.set(0.66 * k, 0.4 * k, 0.5 * k); m.position.set(0, 0.0 * k, 0); }
  }
  // Touché : sursaut (animation de réaction jouée par-dessus, sans couper la marche)
  hit() {
    const a = this.actions.get('HitReact') ?? this.actions.get('hit');
    if (!a || this.dead) return;
    a.setLoop(THREE.LoopOnce, 1);
    a.reset().setEffectiveWeight(0.8).fadeIn(0.05).play();
    setTimeout(() => a.fadeOut(0.2), 250);
  }

  update(dt: number, s: ActorState) {
    this.crouching = s.crouch;
    const fresh = Number.isNaN(this.last.x);
    const dx = fresh ? 0 : s.x - this.last.x, dz = fresh ? 0 : s.z - this.last.y;
    if (dt > 0) { this.vel.x = this.vel.x * 0.75 + (dx / dt) * 0.25; this.vel.y = this.vel.y * 0.75 + (dz / dt) * 0.25; }
    this.speed = this.vel.length();
    this.last.set(s.x, s.z);
    this.root.position.set(s.x, s.y ?? 0, s.z);
    // Le corps tourne vite mais sans à-coup (passage visée ↔ sprint, demi-tour)
    const target = Math.PI / 2 - s.angle;
    if (fresh || s.dead) this.root.rotation.y = target;
    else {
      const diff = Math.atan2(Math.sin(target - this.root.rotation.y), Math.cos(target - this.root.rotation.y));
      this.root.rotation.y += Math.sign(diff) * Math.min(Math.abs(diff), dt * 14);
    }
    if (!this.infected) { this.setWeapon(s.search || s.dead ? null : s.weapon, s.mods); this.setArmor(s.helmet ?? null, s.vest ?? null, s.pack ?? null); }
    if (!this.mixer) { this.body.rotation.x = s.dead ? -Math.PI / 2 : 0; return; }
    if (this.mixamo) this.animateMixamo(s);
    else this.animateQuaternius(s, dt);
    this.mixer.update(dt);
    if (this.mixamo) this.measureChest(s);
    // Pistolet : les animations sont faites pour un fusil, on tend les deux bras vers l'avant
    const shoulder = this.mixamo && !!s.aim && !!this.weapon && !s.dead && !s.sprint && !s.search && !s.reload && !s.heal;
    this.aimW = Math.max(0, Math.min(1, this.aimW + (shoulder ? dt * 9 : -dt * 7)));
    if (this.mixamo && this.weapon === 'pistol' && !s.dead && !s.sprint && !s.search) this.pistolPose(this.aimW);
    else if (this.aimW > 0 && this.weapon) this.shoulderPose(this.aimW);
    this.gestures(dt, s);
    // Buste incliné vers la visée (avec une arme, debout ou accroupi ; pas en courant ni pendant un geste)
    const pitchOn = this.mixamo && !this.infected && !!this.weapon && !s.dead && !s.sprint && !s.search && !s.reload && !s.heal && !s.air;
    this.pitchW += ((pitchOn ? Math.max(-0.9, Math.min(0.9, s.pitch ?? 0)) : 0) - this.pitchW) * Math.min(1, dt * 12);
    this.spinePitch(this.pitchW);
    if (this.held && this.hand) this.placeHeld();
    if (this.recoil > 0) { this.recoil = Math.max(0, this.recoil - dt * 10); this.body.position.z = -this.recoil * 0.07; }
    // Rampant : couché au sol, il avance sur les mains
    if (this.crawl && !s.dead) { this.body.rotation.x = 1.15; this.body.position.y = 0.18; }
  }

  private animateMixamo(s: ActorState) {
    const moving = s.moving || this.speed > 0.4;
    if (s.dead) {
      if (!this.dead) { this.dead = true; this.play(this.infected ? 'death' : this.deathClip, 0.12); }
      return;
    }
    if (this.dead) { this.dead = false; for (const a of this.actions.values()) a.stop(); this.current = null; }
    if (this.infected) {
      this.play(s.attack ? 'attack' : s.sprint ? 'run' : moving ? 'walk' : 'idle', s.attack ? 0.08 : 0.25);
      if (this.current && (this.current === this.actions.get('walk') || this.current === this.actions.get('run'))) this.current.timeScale = Math.max(0.6, Math.min(1.4, this.speed / (s.sprint ? 4 : 1)));
      return;
    }
    if (s.search) { this.play('crouch', 0.25); return; }
    if (this.jumpAnim(s, moving)) return;
    // Sans animations de saut (ancien modèle) : jambes repliées en l'air (la pose accroupie, vite)
    if (s.air) { this.play(s.weapon ? 'crouch_aim' : 'crouch', 0.12); if (this.current) this.current.timeScale = 1; return; }
    if (this.reloadAnim(s, moving)) return;
    if (s.sprint) { this.play('sprint_f', 0.2); if (this.current) this.current.timeScale = Math.max(0.7, Math.min(1.3, this.speed / 5.6)); return; }
    if (moving) {
      // Direction de marche par rapport au regard : de face, de côté, à reculons
      const rel = Math.atan2(this.vel.y, this.vel.x) - s.angle;
      const sector = ((Math.round(rel / (Math.PI / 4)) % 8) + 8) % 8;
      const set = s.crouch ? 'crouch' : s.aim ? 'walk' : 'run';
      this.play(`${set}_${SECTORS[sector]}`, 0.2);
      const nominal = s.crouch ? 1.7 : s.aim ? 2.4 : 3.4;
      if (this.current) this.current.timeScale = Math.max(0.6, Math.min(1.5, this.speed / nominal));
      return;
    }
    this.play(s.crouch ? (s.weapon ? 'crouch_aim' : 'crouch') : s.weapon ? 'aim' : 'idle', 0.25);
    if (this.current) this.current.timeScale = 1;
  }

  // Arme : poignée dans la main droite, canon dans l'axe du regard
  private tmp = new THREE.Vector3();
  private tmp2 = new THREE.Vector3();
  private placeHeld() {
    this.hand!.getWorldPosition(this.tmp);
    this.root.worldToLocal(this.tmp);
    const def = this.weapon ? HELD[this.weapon] : null;
    // Fusil : l'arme suit la ligne main droite → main gauche (comme dans l'animation) ; pistolet : vers l'avant
    const dir = new THREE.Vector3(0, 0, 1);
    if (this.weapon !== 'pistol' && this.leftHand) {
      // Quand les mains se croisent (course), on garde la dernière direction valide au lieu de revenir tout droit
      this.leftHand.getWorldPosition(this.tmp2);
      this.root.worldToLocal(this.tmp2);
      const d = this.tmp2.sub(this.tmp);
      if (d.length() > 0.12 && d.z > 0) this.heldDir.lerp(d.normalize(), 0.35).normalize();
      dir.copy(this.heldDir);
    }
    // Rechargement : arme inclinée (canon vers le bas, tournée vers la main gauche) ; soin : arme baissée le long du corps
    if (this.gesture > 0) {
      const tilt = this.lastHeal ? new THREE.Vector3(0.1, -0.85, 0.5) : new THREE.Vector3(0.3, -0.38, 0.87);
      dir.lerp(tilt.normalize(), this.gesture).normalize();
    }
    this.held!.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
    this.held!.position.copy(this.tmp).addScaledVector(dir, def ? def.len * 0.22 : 0);
  }

  // Oriente un os pour que son os suivant pointe vers une cible (en coordonnées du monde). On tourne l'os depuis sa pose
  // animée du plus petit angle possible : la torsion du bras et du poignet (celle de l'animation) est gardée.
  private aimBone(b: THREE.Object3D, child: THREE.Object3D, target: THREE.Vector3) {
    b.updateWorldMatrix(true, true);
    const from = b.getWorldPosition(new THREE.Vector3());
    const cur = child.getWorldPosition(new THREE.Vector3()).sub(from).normalize();
    const want = target.clone().sub(from).normalize();
    if (cur.lengthSq() < 1e-8 || want.lengthSq() < 1e-8) return;
    const turn = new THREE.Quaternion().setFromUnitVectors(cur, want);
    const parentQ = b.parent!.getWorldQuaternion(new THREE.Quaternion());
    const worldQ = b.getWorldQuaternion(new THREE.Quaternion());
    b.quaternion.copy(parentQ.invert().multiply(turn.multiply(worldQ)));
    b.updateMatrixWorld(true);
  }
  // Visée en hauteur : le buste se penche (réparti sur les trois os du dos), les bras et l'arme suivent
  private pitchW = 0;
  private spinePitch(p: number) {
    if (Math.abs(p) < 1e-3) return;
    const side = new THREE.Vector3(1, 0, 0).transformDirection(this.root.matrixWorld); // axe gauche-droite du personnage
    for (const [n, k] of [['Spine', 0.25], ['Spine1', 0.3], ['Spine2', 0.45]] as const) {
      const b = this.body.getObjectByName(`mixamorig${n}`);
      if (!b) continue;
      b.updateWorldMatrix(true, false);
      const turn = new THREE.Quaternion().setFromAxisAngle(side, -p * k);
      const parentQ = b.parent!.getWorldQuaternion(new THREE.Quaternion());
      b.quaternion.copy(parentQ.invert().multiply(turn.multiply(b.getWorldQuaternion(new THREE.Quaternion()))));
      b.updateMatrixWorld(true);
    }
  }
  // Hauteur de la poitrine par rapport à la pose debout : les points visés par les bras (pistolet, rechargement, soin)
  // sont donnés debout et descendent avec le buste quand le personnage s'accroupit
  private heldDir = new THREE.Vector3(0, 0, 1); // direction du fusil (repère du personnage), lissée
  private aimW = 0; // épaulé (clic droit) : poids de la pose, 0 à 1
  private chestRef = 0;
  private chestDrop = 0;
  private measureChest(s: ActorState) {
    const chest = this.body.getObjectByName('mixamorigSpine2');
    if (!chest) return;
    const y = this.root.worldToLocal(chest.getWorldPosition(this.tmp)).y;
    if (!this.chestRef) this.chestRef = s.crouch ? y + 0.35 : y;
    else if (!s.crouch && !s.dead) this.chestRef += (y - this.chestRef) * 0.05;
    this.chestDrop = Math.min(0, y - this.chestRef);
  }

  // Rechargement et soin : la main gauche suit une suite de points (repère du personnage, mètres), mélangée à l'animation
  private gestures(dt: number, s: ActorState) {
    const busy = !s.dead && !s.search && !s.sprint && !this.infected && this.mixamo;
    this.reloadT = busy && s.reload && this.weapon && !this.reloadClip(s) ? Math.max(0, this.reloadT) + dt : -1;
    this.healT = busy && s.heal && !s.reload ? Math.max(0, this.healT) + dt : -1;
    const on = this.reloadT >= 0 || this.healT >= 0;
    this.gesture = Math.max(0, Math.min(1, this.gesture + (on ? dt * 6 : -dt * 5)));
    if (this.gesture <= 0) return;
    let target: THREE.Vector3;
    if (!on) target = this.lastHand; // fin du geste : retour en douceur depuis le dernier point
    else if (this.reloadT >= 0) {
      // Main gauche : chargeur sous l'arme → poche de poitrine → chargeur → retour en place
      const dur = WEAPONS[this.weapon!].reload;
      const p = (this.reloadT / dur) % 1;
      const pistol = this.weapon === 'pistol';
      const keys: [number, number, number][] = pistol
        ? [[0.05, 1.22, 0.42], [0.2, 0.98, 0.12], [0.2, 0.98, 0.12], [0.04, 1.2, 0.4], [0.03, 1.3, 0.48]]
        : this.weapon === 'sg'
          ? [[0.06, 1.05, 0.42], [0.22, 0.95, 0.12], [0.06, 1.05, 0.42], [0.22, 0.95, 0.12], [0.06, 1.08, 0.45]] // une cartouche à la fois
          : [[0.02, 1.02, 0.36], [0.18, 1.08, 0.16], [0.18, 1.08, 0.16], [0.02, 1.03, 0.38], [0.04, 1.1, 0.44]];
      const f = p * (keys.length - 1), i = Math.min(keys.length - 2, Math.floor(f)), t = f - i, e = t * t * (3 - 2 * t);
      const [a, b] = [keys[i]!, keys[i + 1]!];
      target = new THREE.Vector3(a[0] + (b[0] - a[0]) * e, a[1] + (b[1] - a[1]) * e, a[2] + (b[2] - a[2]) * e);
    } else {
      // Soin : la main gauche enroule un bandage sur l'avant-bras droit / la poitrine (petits cercles)
      const w = this.healT * 7;
      target = new THREE.Vector3(-0.02 + Math.cos(w) * 0.05, 1.12 + Math.sin(w) * 0.04, 0.26 + Math.sin(w * 0.5) * 0.02);
      // Bras droit ramené devant le ventre, arme baissée
    }
    if (on ? this.healT >= 0 : this.lastHeal) this.blendArm('Right', new THREE.Vector3(-0.22, 1.0, 0.12), new THREE.Vector3(-0.06, 1.05, 0.3)); // bras droit devant le ventre
    this.blendArm('Left', new THREE.Vector3(0.3, 1.0, 0.1), target);
    if (on) { this.lastHand.copy(target); this.lastHeal = this.healT >= 0; }
  }
  // Coude vers elbow, main vers hand (repère du personnage), mélangé à la pose animée selon le poids du geste
  private blendArm(side: 'Left' | 'Right', elbow: THREE.Vector3, hand: THREE.Vector3, w = this.gesture) {
    const get = (n: string) => this.body.getObjectByName(`mixamorig${n}`);
    const arm = get(`${side}Arm`), fore = get(`${side}ForeArm`), h = get(`${side}Hand`);
    if (!arm || !fore || !h) return;
    const qa = arm.quaternion.clone(), qf = fore.quaternion.clone();
    this.aimBone(arm, fore, this.root.localToWorld(elbow.clone().setY(elbow.y + this.chestDrop)));
    arm.quaternion.slerpQuaternions(qa, arm.quaternion.clone(), w);
    arm.updateMatrixWorld(true);
    this.aimBone(fore, h, this.root.localToWorld(hand.clone().setY(hand.y + this.chestDrop)));
    fore.quaternion.slerpQuaternions(qf, fore.quaternion.clone(), w);
    fore.updateMatrixWorld(true);
  }

  // Fusil épaulé : crosse à l'épaule droite, main gauche loin sur le garde-main, arme à hauteur des yeux
  private shoulderPose(w: number) {
    this.blendArm('Right', new THREE.Vector3(-0.34, 1.3, 0.0), new THREE.Vector3(-0.1, 1.43, 0.2), w);
    this.blendArm('Left', new THREE.Vector3(0.12, 1.24, 0.34), new THREE.Vector3(-0.06, 1.45, 0.56), w);
    // Tête penchée sur la crosse
    const head = this.body.getObjectByName('mixamorigHead');
    if (head) { head.rotateX(0.22 * w); head.rotateZ(-0.12 * w); head.updateMatrixWorld(true); }
  }

  // Tenue du pistolet à deux mains, bras tendus à hauteur de poitrine (épaulé : à hauteur des yeux, plus tendus)
  private pistolPose(aim = 0) {
    const get = (n: string) => this.body.getObjectByName(`mixamorig${n}`);
    const at = (x: number, y: number, z: number) => this.root.localToWorld(new THREE.Vector3(x, y + this.chestDrop, z));
    for (const [side, hx, ex] of [['Right', -0.03, -0.2], ['Left', 0.03, 0.2]] as const) {
      const arm = get(`${side}Arm`), fore = get(`${side}ForeArm`), hand = get(`${side}Hand`);
      if (!arm || !fore || !hand) continue;
      const hy = (side === 'Right' ? 1.36 : 1.33) + aim * 0.1;
      this.aimBone(arm, fore, at(ex, 1.22 + aim * 0.08, 0.22 + aim * 0.06));
      this.aimBone(fore, hand, at(hx, hy, 0.5 + aim * 0.08));
    }
  }


  private animateQuaternius(s: ActorState, dt: number) {
    if (s.dead) {
      if (!this.dead) { this.dead = true; this.play('Death', 0.1); }
    } else {
      if (this.dead) { this.dead = false; this.actions.get('Death')?.stop(); this.current = null; }
      const moving = s.moving || this.speed > 0.4;
      if (this.infected) this.play(s.attack ? 'Punch' : s.sprint ? 'Run_Arms' : moving ? 'Walk' : 'Idle', s.attack ? 0.08 : 0.2);
      else if (s.search) this.play(this.actions.has('Duck') ? 'Duck' : 'Idle', 0.25);
      else this.play(s.sprint ? 'Run_Gun' : moving ? 'Walk_Gun' : 'Idle_Gun');
      const walk = this.current === this.actions.get('Walk_Gun') || this.current === this.actions.get('Walk');
      if (this.current) this.current.timeScale = walk ? Math.max(0.6, Math.min(1.5, this.speed / (this.infected ? 1.2 : 2.4))) : 1;
    }
    // Accroupi : silhouette abaissée (pas d'animation dédiée dans ce pack)
    const k = s.crouch && !s.dead ? 0.82 : 1;
    const cur = this.body.scale.y / this.scale;
    this.body.scale.y = this.scale * (cur + (k - cur) * Math.min(1, dt * 12));
  }

  dispose() { this.root.removeFromParent(); }
}
